import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContext';
import { supabase, dbUserToAppUser, appUpdateToDbUpdate } from '../lib/supabase';
import { trackEvent, trackConversion, identifyUser, resetUser } from '../lib/analytics';
import { buildUtmProfilePatch } from '../lib/utm';
import { notifyAdmin } from '../lib/notify';

const UserContext = createContext();

export const useUser = () => useContext(UserContext);

/* ————————————————————————————————————————————————————————————————————————
   EAGER Score calculator — identical logic to the original Firebase version
   (gameplay & points math MUST stay the same)
———————————————————————————————————————————————————————————————————————— */
function calculateEagerScoreFn(profile) {
    let score = 0;
    const academic = profile.academic || {};
    const quiz = profile.quizResult || {};

    if (profile.name) score += 40;
    if (profile.avatar && !String(profile.avatar).includes('ui-avatars')) score += 70;
    if (profile.location) score += 35;
    if (profile.title && profile.title !== 'Eager Explorer') score += 35;

    const marks12 = parseFloat(academic.marks12) || 0;
    if (marks12 > 0) score += (marks12 / 100) * 80;

    if (academic.stream) score += 40;
    if (academic.marks10) score += 40;
    if (academic.cgpaUG) score += 40;
    if (academic.cgpaPG) score += 40;

    if (quiz.code) score += 120;

    const interestsCount = (profile.interests || []).length;
    if (interestsCount >= 3) score += 60;
    else if (interestsCount > 0) score += 30;

    const skillsCount = (profile.skills || []).length;
    score += Math.min(skillsCount * 25, 100);

    const projects = profile.projects || [];
    score += Math.min(projects.length * 45, 135);

    const badges = profile.badges || [];
    if (badges.length > 1) score += 45;
    if (badges.includes('Scout Elite')) score += 40;

    if (profile.savedColleges?.length > 0) {
        score += Math.min(profile.savedColleges.length * 10, 50);
    }

    return Math.min(Math.round(score), 1000);
}

export const UserProvider = ({ children }) => {
    const [isLoading, setIsLoading] = useState(true);
    // Tracks whether the FIRST boot-time hydration has completed.  We use this
    // to gate the full-screen loading spinner — subsequent hydrations (e.g.
    // after a fresh registration) must NOT unmount children, because that
    // throws away in-progress component state (Quiz flow being the obvious
    // victim).  See iteration_10 RCA.
    const [hasBooted, setHasBooted] = useState(false);
    const [user, setUser] = useState(null);
    const [savedColleges, setSavedColleges] = useState([]);
    const [compareList, setCompareList] = useState([]);
    const [applications, setApplications] = useState([]);
    const [simulatedRole, setSimulatedRole] = useState(null); // Admin "View as" override
    const { addToast } = useToast();

    /* —— Load or auto-create the user's `public.users` row on sign-in —— */
    const hydrateProfile = useCallback(async (authUser) => {
        if (!authUser) return null;
        try {
            // Upsert — insert if first time, no-op if exists (matches=id)
            const baseline = {
                id: authUser.id,
                email: authUser.email,
                name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || 'User',
                avatar: authUser.user_metadata?.avatar_url || authUser.user_metadata?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(authUser.user_metadata?.name || 'User')}&background=random`,
                role: 'student',
                title: 'Eager Explorer',
                interests: [],
                skills: [],
                projects: [],
                saved_colleges: [],
                applications: [],
                academic: { marks10: '', marks12: '', cgpaUG: '', cgpaPG: '', stream: '', state: '' },
                quiz_result: null,
                quiz_attempts: 0,
                eager_score: 0,
                badges: ['Newcomer'],
                referral_count: 0,
                stats: { profileViews: 0, shortlisted: 0, applications: 0 },
                scout_mode: false,
                joined_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            };
            await supabase.from('users').upsert(baseline, { onConflict: 'id', ignoreDuplicates: true });

            // Detect FIRST-TIME signup so we ping Saleem on WhatsApp. We do a
            // lightweight SELECT before the upsert would be cleaner but adds a
            // round-trip on every hydrate. Instead we rely on `joined_at` —
            // if it matches our baseline (i.e. the upsert actually inserted),
            // we know this is a new user. Since ignoreDuplicates=true, an
            // existing row will already have an older joined_at.
            try {
                const { data: postRow } = await supabase
                    .from('users').select('joined_at, name, email, phone')
                    .eq('id', authUser.id).maybeSingle();
                if (postRow && postRow.joined_at === baseline.joined_at) {
                    // First-touch — the upsert just inserted this row.
                    notifyAdmin('signup', {
                        name: postRow.name || baseline.name,
                        email: postRow.email || baseline.email,
                        phone: postRow.phone || authUser.phone || '—',
                    });
                }
            } catch { /* notify is best-effort */ }

            // ── UTM attribution (first-touch) ──────────────────────────────
            // If the user landed via a campaign link, store the source on
            // their profile so the admin Signups dashboard can show
            // attribution. Best-effort: failure must not block hydration.
            try {
                const utmPatch = buildUtmProfilePatch();
                if (utmPatch) {
                    // Only attach if not already attributed — keep first-touch sticky.
                    const { data: existingUtm } = await supabase
                        .from('users').select('utm_source').eq('id', authUser.id).maybeSingle();
                    if (!existingUtm?.utm_source) {
                        await supabase.from('users').update(utmPatch).eq('id', authUser.id);
                    }
                }
            } catch (e) {
                console.warn('[UserContext] utm attach skipped:', e?.message);
            }

            const { data, error } = await supabase.from('users').select('*').eq('id', authUser.id).single();
            if (error) throw error;
            return dbUserToAppUser(data);
        } catch (e) {
            console.warn('[UserContext] hydrateProfile failed:', e?.message || e);
            // Fallback: build a minimal in-memory profile so the app still boots
            return dbUserToAppUser({
                id: authUser.id,
                email: authUser.email,
                name: authUser.user_metadata?.full_name || 'User',
                avatar: authUser.user_metadata?.avatar_url || '',
                role: 'student',
            });
        }
    }, []);

    /* —— Auth lifecycle —— */
    useEffect(() => {
        let mounted = true;
        let lastHydratedId = null;

        // Safety: force-exit loading after 3s if Supabase hangs
        const safety = setTimeout(() => {
            if (mounted) setIsLoading(false);
        }, 3000);

        supabase.auth.getSession().then(async ({ data: { session } }) => {
            if (!mounted) return;
            if (session?.user) {
                lastHydratedId = session.user.id;
                const profile = await hydrateProfile(session.user);
                if (!mounted) return;
                setUser(profile);
                setSavedColleges(profile?.savedColleges || []);
                setApplications(profile?.applications || []);
                identifyUser({
                    id: profile?.id || session.user.id,
                    email: profile?.email || session.user.email,
                    role: profile?.role,
                    fullName: profile?.fullName,
                });
            }
            setIsLoading(false);
            setHasBooted(true);
            clearTimeout(safety);
        }).catch(err => {
            console.warn('[UserContext] getSession error:', err?.message);
            if (mounted) { setIsLoading(false); setHasBooted(true); }
        });

        const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
            if (!mounted) return;
            if (session?.user) {
                const sameUser = lastHydratedId === session.user.id;
                if (!sameUser || event === 'SIGNED_IN' || event === 'USER_UPDATED') {
                    lastHydratedId = session.user.id;
                    // Keep loading TRUE while profile hydrates so Profile page
                    // doesn't briefly flash the "Ghost Profile" screen.
                    setIsLoading(true);
                    hydrateProfile(session.user).then((profile) => {
                        if (!mounted) return;
                        setUser(profile);
                        setSavedColleges(profile?.savedColleges || []);
                        setApplications(profile?.applications || []);
                        identifyUser({
                            id: profile?.id || session.user.id,
                            email: profile?.email || session.user.email,
                            role: profile?.role,
                            fullName: profile?.fullName,
                        });
                        // Detect first-time signup (Google OAuth path doesn't
                        // hit our register() / verifyOtp() conversion firing).
                        // If created_at ≈ last_sign_in_at (within 60s), this is
                        // a brand-new account → fire the Lead conversion.
                        try {
                            const u = session.user;
                            const created = u.created_at ? new Date(u.created_at).getTime() : 0;
                            const lastIn  = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
                            const isNewSignup = created && lastIn && (lastIn - created) < 60_000;
                            if (isNewSignup) {
                                const provider = u.app_metadata?.provider || 'unknown';
                                trackConversion('Lead', { method: provider });
                            }
                        } catch (e) { /* silent */ }
                        // After first-time signup, credit the stored referrer (if any).
                        tryCreditPendingReferral(profile?.id || session.user.id);
                        setIsLoading(false);
                    }).catch(() => {
                        if (mounted) setIsLoading(false);
                    });
                    return; // Don't call setIsLoading(false) below
                }
            } else {
                setUser(null);
                setSavedColleges([]);
                setApplications([]);
                lastHydratedId = null;
                setSimulatedRole(null);
                resetUser(); // clear PostHog/Sentry user identity on sign-out
            }
            setIsLoading(false);
        });

        return () => {
            mounted = false;
            clearTimeout(safety);
            sub.subscription.unsubscribe();
        };
    }, [hydrateProfile]);

    /* —————— AUTH ACTIONS —————— */
    const login = async (email, password) => {
        trackEvent('auth', 'login_attempt', 'email');
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            trackEvent('auth', 'login_failed', 'email');
            throw error;
        }
        trackEvent('auth', 'login_success', 'email');
        return data.user;
    };

    const register = async (name, email, password, phone = '') => {
        trackEvent('auth', 'signup_attempt', 'email');
        const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: { data: { full_name: name, name, phone } },
        });
        if (error) {
            trackEvent('auth', 'signup_failed', 'email');
            throw error;
        }
        trackEvent('auth', 'signup_success', 'email');
        // Fire STANDARD 'Lead' conversion for Meta + GA4 + Google Ads bidding.
        trackConversion('Lead', { method: 'email' });
        // Profile row will be created by onAuthStateChange→hydrateProfile.
        // Patch name + phone now so admin dashboard has them instantly:
        if (data.user) {
            try {
                await supabase.from('users').upsert({
                    id: data.user.id,
                    email,
                    name,
                    phone: phone || null,
                    role: 'student',
                    updated_at: new Date().toISOString(),
                }, { onConflict: 'id' });
            } catch (e) {
                console.warn('[UserContext] post-signup profile patch failed:', e?.message);
            }
        }
        return data.user;
    };

    const loginWithGoogle = async (returnTo) => {
        trackEvent('auth', 'login_attempt', 'google');
        // Flag so PostLoginRedirect knows to send user back to the right page.
        // Optional `returnTo` arg lets callers (e.g. Quiz) say "send me back here".
        // Defensive: callers sometimes pass `onClick={loginWithGoogle}` directly,
        // which would forward the React click-event object. Coerce anything that
        // isn't a clean path-string back to a safe default.
        const safeReturnTo = (typeof returnTo === 'string' && returnTo.startsWith('/'))
            ? returnTo
            : '/profile';
        try {
            sessionStorage.setItem('eager_just_signed_in', '1');
            sessionStorage.setItem('eager_return_to', safeReturnTo);
        } catch (e) {
            // sessionStorage can fail in private mode / disabled storage. Non-fatal — fall back to /profile.
            console.warn('[UserContext] sessionStorage write failed:', e?.message);
        }
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            // Land at root — HashRouter will handle in-app routing after session loads.
            options: { redirectTo: `${window.location.origin}/` },
        });
        if (error) throw error;
    };

    const logout = async () => {
        trackEvent('auth', 'logout', 'manual');
        await supabase.auth.signOut();
        setSimulatedRole(null);
    };

    /* —————— DATA ACTIONS —————— */
    const toggleSave = async (collegeId) => {
        const isSaved = savedColleges.includes(collegeId);
        const newSaved = isSaved
            ? savedColleges.filter(id => id !== collegeId)
            : [...savedColleges, collegeId];
        setSavedColleges(newSaved);
        if (user) {
            try {
                await supabase.from('users').update({
                    saved_colleges: newSaved,
                    updated_at: new Date().toISOString(),
                }).eq('id', user.id);
            } catch (e) { console.warn('toggleSave:', e?.message); }
        }
    };

    const toggleCompare = (collegeId) => {
        setCompareList(prev => {
            if (prev.includes(collegeId)) return prev.filter(id => id !== collegeId);
            if (prev.length >= 3) {
                alert('You can compare up to 3 colleges at a time.');
                return prev;
            }
            return [...prev, collegeId];
        });
    };

    const clearCompare = () => setCompareList([]);

    const applyToCollege = async (collegeId) => {
        if (!user) {
            addToast('Please login to apply', 'error');
            return;
        }
        if (applications.some(app => app.collegeId === collegeId)) {
            addToast('You have already applied here!', 'info');
            return;
        }
        const newApplication = {
            collegeId,
            status: 'applied',
            appliedAt: new Date().toISOString(),
            grantStatus: 'pending',
        };
        const newApps = [...applications, newApplication];
        setApplications(newApps);
        const newStats = { ...(user.stats || {}), applications: (user.stats?.applications || 0) + 1 };
        try {
            const { error } = await supabase.from('users').update({
                applications: newApps,
                stats: newStats,
                updated_at: new Date().toISOString(),
            }).eq('id', user.id);
            if (error) throw error;
            setUser(prev => ({ ...prev, applications: newApps, stats: newStats }));
            addToast('Application Sent! Check your profile.', 'success');
        } catch (error) {
            console.error('Application Failed:', error);
            addToast('Application failed to save', 'error');
            setApplications(prev => prev.filter(app => app.collegeId !== collegeId));
        }
    };

    const updateUser = async (newData) => {
        if (!user) return;
        // Merge academic partial updates
        const updatedProfile = { ...user, ...newData };
        if (newData.academic && user.academic) {
            updatedProfile.academic = { ...user.academic, ...newData.academic };
            newData.academic = updatedProfile.academic;
        }
        // Merge stats partial updates (legacy Firestore path "stats.applications")
        if (newData.stats && user.stats) {
            updatedProfile.stats = { ...user.stats, ...newData.stats };
            newData.stats = updatedProfile.stats;
        }
        // Recalculate score if relevant fields changed
        const newScore = calculateEagerScoreFn(updatedProfile);
        if (newScore !== updatedProfile.eagerScore) {
            updatedProfile.eagerScore = newScore;
            newData.eagerScore = newScore;
        }
        try {
            const patch = appUpdateToDbUpdate(newData);
            const { error } = await supabase.from('users').update(patch).eq('id', user.id);
            if (error) throw error;
            setUser(updatedProfile);
            if (newData.savedColleges) setSavedColleges(newData.savedColleges);
            // Badge check
            checkBadges(updatedProfile);
        } catch (error) {
            console.error('Profile Update Failed:', error);
            addToast('Failed to save changes', 'error');
        }
    };

    const addReferral = async () => {
        if (!user) return;
        const count = (user.referralCount || 0) + 1;
        await updateUser({ referralCount: count });
    };

    // Credit a referrer atomically via SECURITY DEFINER RPC.
    // Called once after first-time signup if a `?ref=` param was captured
    // earlier and stashed in sessionStorage (see App.jsx → useReferralCapture).
    const tryCreditPendingReferral = async (newUserId) => {
        try {
            const referrerId = typeof sessionStorage !== 'undefined'
                ? sessionStorage.getItem('eager_pending_referrer') : null;
            if (!referrerId) return;
            sessionStorage.removeItem('eager_pending_referrer');
            if (!newUserId || referrerId === newUserId) return;
            await supabase.rpc('credit_referral', { p_referrer_id: referrerId });
        } catch (e) {
            // Non-fatal — referral simply doesn't credit
            console.warn('[UserContext] credit_referral failed:', e?.message || e);
        }
    };

    const submitQuiz = async (quizData) => {
        if (!user) return;
        const currentAttempts = user.quizAttempts || 0;
        await updateUser({ quizResult: quizData, quizAttempts: currentAttempts + 1 });
    };

    const acknowledgeDisclaimer = async () => {
        if (!user) return;
        const now = new Date().toISOString();
        // Optimistic UI update — close the modal immediately, even if DB write is slow
        setUser(prev => prev ? { ...prev, lastAcknowledgedAt: now } : prev);
        // Best-effort persist
        try {
            await supabase.from('users')
                .update({ last_acknowledged_at: now, updated_at: now })
                .eq('id', user.id);
        } catch (err) {
            console.warn('acknowledgeDisclaimer persist failed:', err?.message);
            // UI already updated — user can continue; next save will retry
        }
    };

    const getReadinessBand = (score) => {
        if (score <= 300) return { name: 'Foundation Stage', color: 'text-slate-400', bg: 'bg-slate-100', border: 'border-slate-200' };
        if (score <= 600) return { name: 'Skill-Growth Ready', color: 'text-blue-500', bg: 'bg-blue-50', border: 'border-blue-200' };
        if (score <= 850) return { name: 'Industry Ready', color: 'text-indigo-500', bg: 'bg-indigo-50', border: 'border-indigo-200' };
        return { name: 'Scout Elite', color: 'text-emerald-500', bg: 'bg-emerald-50', border: 'border-emerald-200' };
    };

    const checkBadges = async (currentProfile) => {
        const badges = new Set(currentProfile.badges || ['Newcomer']);
        if (currentProfile.eagerScore >= 500) badges.add('Academic Weapon');
        if ((currentProfile.skills || []).length >= 3) badges.add('Skill Master');
        if ((currentProfile.projects || []).length >= 2) badges.add('Builder');
        if (currentProfile.savedColleges?.length >= 5) badges.add('Researcher');
        if (badges.size > (currentProfile.badges || []).length) {
            const newBadges = Array.from(badges);
            const oldSet = new Set(currentProfile.badges || []);
            const newlyUnlockedName = newBadges.find(b => !oldSet.has(b));
            try {
                await supabase.from('users').update({
                    badges: newBadges,
                    updated_at: new Date().toISOString(),
                }).eq('id', currentProfile.id);
                setUser(prev => ({ ...prev, badges: newBadges }));
                if (newlyUnlockedName) addToast(`New Milestone Unlocked: ${newlyUnlockedName}`, 'milestone');
            } catch (e) { console.warn('checkBadges:', e?.message); }
        }
    };

    const updateEagerScore = async () => {
        if (!user) return;
        const newScore = calculateEagerScoreFn(user);
        const updated = { ...user, eagerScore: newScore };
        if (newScore !== user.eagerScore) {
            try {
                await supabase.from('users').update({
                    eager_score: newScore,
                    updated_at: new Date().toISOString(),
                }).eq('id', user.id);
                setUser(prev => ({ ...prev, eagerScore: newScore }));
            } catch (e) { console.warn('updateEagerScore:', e?.message); }
        }
        await checkBadges(updated);
    };

    /* —————— Admin "View as" role switcher —————— */
    const setViewAsRole = (role) => setSimulatedRole(role);
    const clearViewAsRole = () => setSimulatedRole(null);

    // User object the rest of the app sees — apply simulated role if admin
    const effectiveUser = simulatedRole && user?.role === 'admin'
        ? { ...user, role: simulatedRole, _simulated: true, _realRole: 'admin' }
        : user;

    const value = {
        isLoading,
        hasBooted,
        userData: effectiveUser,
        user: effectiveUser,
        realUser: user,
        savedColleges,
        compareList,
        login,
        register,
        loginWithGoogle,
        logout,
        toggleSave,
        toggleCompare,
        clearCompare,
        updateUser,
        addReferral,
        updateEagerScore,
        getReadinessBand,
        submitQuiz,
        acknowledgeDisclaimer,
        shortlist: savedColleges,
        applications,
        applyToCollege,
        // View-as
        simulatedRole,
        setViewAsRole,
        clearViewAsRole,
    };

    return (
        <UserContext.Provider value={value}>
            {(isLoading && !hasBooted) ? (
                <div className="min-h-screen flex items-center justify-center bg-slate-50">
                    <div className="flex flex-col items-center gap-4">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                        <div className="text-slate-500 font-bold animate-pulse">Loading Eager...</div>
                    </div>
                </div>
            ) : (
                children
            )}
        </UserContext.Provider>
    );
};
